import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Container, Badge, Card, CardHeader, CardBody,
  Button, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup,
  Label, Input, Alert,
  Row,
  Col
} from "reactstrap";
import { upgradeDowngradeSubscription, viewUser } from "./_requests";
import { toast } from "react-toastify";
import moment from "moment";
import TableContainer from "../../Components/Common/TableContainer";
import UpgradeDowngradeModal from "./UpgradeDowngradeModal";
import { formatDate } from "../../helpers/FunctionHelpers";

interface UserSubscriptionListProps {
  refresh: boolean;
  userId?: number;
}

interface Subscription {
  id: number;
  user_id: number;
  addressbook_id: number;
  shipping_address: string;
  subscription_id: number;
  type: string;
  type_name: string;
  slot_type: number;
  slot_type_name: string;
  payment_id: string | null;
  gross_amount: number;
  start_date: string;
  end_date: string;
  payment_status: string;
  is_active: number;
  remaining_days_plan: number;
  receipt: string;
  addressbook: {
    id: number;
    address_type: string;
    is_default: number;
    longitude: number;
    latitude: number;
    user_id: number;
    address: string;
    land_mark: string;
    city: string;
    state: string;
    country: string;
    postal_code: number;
    mobile_number: string;
  };
  user: {
    id: number;
    name: string;
    email: string;
    mobile_number: string;
  };
  route: {
    name: string;
  };
  store: {
    name: string;
  };
  subscription: {
    id: number;
    name: string;
    type: number;
    image: string;
    plan_feature_id: string;
    description: string;
  };
  details: {
    id: number;
    user_subscription_id: number;
    product_id: number;
    variant_id: number;
    variant_value: number;
    validity: number;
    validity_type: string;
    product: {
      id: number;
      name: string;
      icon: string;
    };
    variant: {
      id: number;
      name: string;
    };
  }[];
}

const UserSubscriptionList = ({ refresh, userId }: UserSubscriptionListProps) => {
  const { id } = useParams<{ id: string }>();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [filteredSubscriptions, setFilteredSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
  const [isUpgradeDowngradeModalOpen, setIsUpgradeDowngradeModalOpen] = useState(false);
  const [filterType, setFilterType] = useState<string>("all"); // "all", "current", "expired"

  const handleUpgradeDowngrade = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setIsUpgradeDowngradeModalOpen(true);
  };

  const handleUpgradeDowngradeSubmit = async (data: any) => {
    try {
      const result = await upgradeDowngradeSubscription(data);
      toast.success(result?.data?.message || "Subscription updated successfully!");
      fetchSubscriptions();
    } catch (error) {
      toast.error("Failed to update subscription");
      console.error(error);
    }
  };

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const response = await viewUser(id || '');
      const allSubscriptions = response?.subscriptions || [];
      setSubscriptions(allSubscriptions);
      applyFilter(allSubscriptions, filterType);
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilter = (data: Subscription[], type: string) => {
    let filtered = [...data];
    if (type === "current") {
      filtered = data.filter(s => s.is_active === 1);
    } else if (type === "expired") {
      filtered = data.filter(s => s.is_active === 0);
    }
    setFilteredSubscriptions(filtered);
  };

  useEffect(() => {
    fetchSubscriptions();
  }, [id, refresh, userId]);

  useEffect(() => {
    applyFilter(subscriptions, filterType);
  }, [filterType, subscriptions]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "Paid":
        return <Badge color="success">Paid</Badge>;
      case "Pending":
        return <Badge color="warning">Pending</Badge>;
      case "Failed":
        return <Badge color="danger">Failed</Badge>;
      default:
        return <Badge color="secondary">{status}</Badge>;
    }
  };

  const getActiveBadge = (isActive: number) => {
    return isActive ? (
      <Badge color="success">Active</Badge>
    ) : (
      <Badge color="danger">Expired</Badge>
    );
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const columns = [
    {
      header: "#",
      accessorKey: "id",
      cell: (cell: any) => {
        const { pageIndex, pageSize } = cell.table.getState().pagination;
        return pageIndex * pageSize + cell.row.index + 1;
      },
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      header: "Subscription",
      accessorKey: "subscription.name",
      cell: (cell: any) => (
        <div>
          <strong>{cell.row.original.subscription?.name}</strong>
          <div className="mt-1">
            <Badge color="info">{cell.row.original.type_name}</Badge>
          </div>
        </div>
      ),
    },
    {
      header: "Slot/Store/Route",
      cell: (cell: any) => (
        <div>
          <Badge color="primary">{cell.row.original.slot_type_name}</Badge><br />
          <span className="text-muted">{cell.row.original.route?.name}</span><br />
          <span className="text-muted">{cell.row.original.store?.name}</span>
        </div>
      ),
    },
    {
      header: "Products",
      cell: (cell: any) => (
        <div className="d-flex flex-column">
          {cell.row.original.details?.map((detail: any) => (
            <div key={detail.id} className="mb-1">
              <strong>{detail.product?.name}</strong> - {detail.variant_value} {detail.variant?.name}
              <div>
                <small className="text-muted">
                  Validity: {detail.validity} {detail.validity_type}
                </small>
              </div>
            </div>
          ))}
        </div>
      ),
    },
    {
      header: "Amount",
      cell: (cell: any) => (
        <div>
          {formatCurrency(cell.row.original.gross_amount)}<br />
          {getStatusBadge(cell.row.original.payment_status)}
        </div>
      ),
    },
    {
      header: "Status & Duration",
      cell: (cell: any) => (
        <div>
          <div className="mb-2">{getActiveBadge(cell.row.original.is_active)}</div>
          {cell.row.original.is_active === 1 && (
            <Badge color={cell.row.original.remaining_days_plan > 7 ? "success" : "warning"}>
              {cell.row.original.remaining_days_plan} days left
            </Badge>
          )}
          <br />
          <strong>{formatDate(cell.row.original.start_date)}</strong> -
          <br />
          <strong>{formatDate(cell.row.original.end_date)}</strong>
        </div>
      ),
    },
    {
      header: "Payment Details",
      accessorKey: "payment_details",
      enableColumnFilter: false,
      cell: (cell: any) => {
        const order = cell.row.original;
        const paymentTypeText = order.payment_type == 1 ? "Online" : "Cash in Hand";
        const paymentTypeColor = order.payment_type == 1 ? "success" : "warning"; 
        const description = order.description || "";
        const words = description.split(' ');
        const shortDescription = words.slice(0, 5).join(' ') + (words.length > 5 ? '...' : '');

        const descriptionId = `description-${order.id}`;
        const toggleId = `toggle-${order.id}`;

        const toggleDescription = () => {
          const descElement = document.getElementById(descriptionId);
          const toggleElement = document.getElementById(toggleId);

          if (descElement && toggleElement) {
            if (descElement.textContent === shortDescription) {
              descElement.textContent = description;
              toggleElement.textContent = 'Read less';
            } else {
              descElement.textContent = shortDescription;
              toggleElement.textContent = 'Read more';
            }
          }
        };

        return (
          <div className="payment-details-cell">
            <div className="mb-1">
              <Badge color={paymentTypeColor} className="fs-12">
                {paymentTypeText}
              </Badge>
            </div>
            {order.payment_type == 1 && order.reference_payment_id && (
              <div className="mb-1">
                <strong className="text-muted">Ref ID:</strong>{' '}
                <span className="text-primary">{order.reference_payment_id}</span>
              </div>
            )}
            {description && (
              <div>
                <strong className="text-muted">Description:</strong>{' '}
                <span id={descriptionId} className="text-dark">
                  {shortDescription}
                </span>
                {words.length > 5 && (
                  <button
                    type="button"
                    id={toggleId}
                    className="btn btn-link p-0 ms-1 text-primary"
                    onClick={toggleDescription}
                    style={{ fontSize: '12px' }}
                  >
                    Read more
                  </button>
                )}
              </div>
            )}
          </div>
        );
      }
    },
    {
      header: "Address",
      accessorKey: "shipping_address",
    },
    {
      header: "Upgrade / Downgrade",
      cell: (cell: any) => {
        const data = cell.row.original;
        const reason = data.reason || "";
        if (!data.is_upgrade_downgrade) return "-";
        
        return (
          <div>
            <Badge color={data.action_type === "upgrade" ? "success" : "warning"}>
              {data.action_type}
            </Badge>
            <br />
            <strong>
              {data.adjusted_remaining_days} days left (
              {data.new_variant_value}  {data.new_variant?.name})
            </strong>
            <br />
            <strong>{formatDate(data.new_end_date)}</strong>
          </div>
        );
      },
    },
    {
      header: "Actions",
      cell: (cell: any) => (
        <div>
          {cell.row.original.is_trial_plan == false && cell.row.original.is_upgrade_downgrade == false &&
            <Button
              color="primary"
              size="sm"
              onClick={() => handleUpgradeDowngrade(cell.row.original)}
              disabled={cell.row.original.is_active != 1}
              className="mb-1"
            >
              Upgrade / Downgrade
            </Button>
          }
        </div>
      ),
    }
  ];

  return (
    <div className="">
      <Container fluid>
        <Card>
          <CardHeader className="d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">Subscription History</h5>
            <div className="d-flex gap-2">
               <Button
                 color={filterType === "all" ? "primary" : "outline-primary"}
                 size="sm"
                 onClick={() => setFilterType("all")}
               >
                 All Status
               </Button>
               <Button
                 color={filterType === "current" ? "success" : "outline-success"}
                 size="sm"
                 onClick={() => setFilterType("current")}
               >
                 Current
               </Button>
               <Button
                 color={filterType === "expired" ? "danger" : "outline-danger"}
                 size="sm"
                 onClick={() => setFilterType("expired")}
               >
                 Expired
               </Button>
            </div>
          </CardHeader>
          <CardBody>
            {loading ? (
              <div className="text-center">Loading subscriptions...</div>
            ) : filteredSubscriptions.length > 0 ? (
              <>
                <TableContainer
                  columns={columns}
                  data={filteredSubscriptions}
                  isGlobalFilter={true}
                  customPageSize={10}
                  divClass="table-responsive mb-1"
                  tableClass="mb-0 table-bordered"
                  theadClass="table-light text-muted"
                />
                <UpgradeDowngradeModal
                  isOpen={isUpgradeDowngradeModalOpen}
                  toggle={() => setIsUpgradeDowngradeModalOpen(false)}
                  subscription={selectedSubscription}
                  onUpgradeDowngrade={handleUpgradeDowngradeSubmit}
                />
              </>
            ) : (
              <div className="text-center py-5">
                <h5>No subscriptions found for this criteria</h5>
              </div>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default UserSubscriptionList;